import { builder } from '@atlassian/state-machine/builder';
import { always } from '@atlassian/state-machine/common/guards';
import type { Machine } from '@atlassian/state-machine/types';
import type AnalyticsWebClient from '@atlassiansox/analytics-web-client';
import { nanoid } from 'nanoid';

import { setTags } from '@canvas/sentry-react';

import { type CollabStrategy } from '../collab-strategy';
import { type RemoteDocument } from '../connection';
import { type Queue } from '../network/queue';
import type { RelaySupplierResult } from '../network/socket';
import { type StateBridge } from '../network/state-bridge';

import {
  type Debounce,
  generateTraceId,
  type PendingMessage,
  type SocketMachineContext,
} from './network-state-machine-context';
import { type Events, type EventsLookup } from './network-state-machine-events';
import { activeState, type ActiveStates } from './states/active/active-state';

export type State = ActiveStates | 'init' | 'destroyed';

export type SocketStateMachine = Machine<
  State,
  Events,
  EventsLookup,
  SocketMachineContext
>;

export interface SocketStateMachineConfig {
  sessionId?: string;
  getAnalyticsWebClient?: () => Promise<AnalyticsWebClient>;
  stateBridge?: StateBridge;
  socketFetchSnapshot(signal: AbortSignal): Promise<RemoteDocument>;
  socketFetchSteps(
    remoteDocument: RemoteDocument,
    attempt: number,
    signal: AbortSignal,
  ): Promise<RemoteDocument>;
  socketApplyChanges(relayUrl: string, remoteDoc: RemoteDocument): RemoteDocument;
  socketUpdateChanges(
    remoteDocument: RemoteDocument,
    connectionAttempts: number,
    signal: AbortSignal,
  ): void;
  socketConnect?(): void;
  onAckTimeout?(): void;
  accessTokenSupplier(): Promise<string>;
  relaySupplier(
    attempt: number,
    signal: AbortSignal,
    collabStrategy: CollabStrategy,
  ): RelaySupplierResult;

  signal: AbortSignal;
  debounce: Debounce;

  shouldHaltRetry: () => boolean;
  pendingMessages: Queue<PendingMessage>;
  shouldSkipUpdates: () => boolean;
  reconcileOfflineChanges?(remoteDocument: RemoteDocument): Promise<void>;
}

export function createNetworkStateMachine({
  sessionId,
  getAnalyticsWebClient,
  stateBridge,
  socketFetchSnapshot,
  socketFetchSteps,
  socketApplyChanges,
  socketUpdateChanges,
  socketConnect,
  onAckTimeout,
  accessTokenSupplier,
  relaySupplier,
  signal,
  debounce,
  shouldHaltRetry,
  pendingMessages,
  shouldSkipUpdates,
  reconcileOfflineChanges,
}: SocketStateMachineConfig): SocketStateMachine {
  pendingMessages.setPredicate((message) => message.enqueue);

  const machine = builder<State, Events, EventsLookup, SocketMachineContext>()
    .initial('init')
    .context({
      sessionId,
      getAnalyticsWebClient,
      stateBridge,
      socketFetchSnapshot,
      socketFetchSteps,
      socketApplyChanges,
      socketUpdateChanges,
      socketConnect,
      onAckTimeout,
      accessTokenSupplier,
      relaySupplier,
      traceId: nanoid(),
      signal,
      debounce,
      pendingMessages,
      shouldHaltRetry,
      shouldSkipUpdates,
      reconcileOfflineChanges,
    })
    .state('init', (s) =>
      s.when('start', (t) =>
        t.if(always).context(generateTraceId).to('connecting:waiting'),
      ),
    )
    .state('active', activeState)
    .state('destroyed', (s) => s)
    .build();

  machine.onEvent((ev) => {
    machine.send(ev);
  });

  machine.onTransition((_ev, _ctx, { current }) => {
    setTags({
      'canvas:network-connection': current,
    });
  });

  signal.addEventListener(
    'abort',
    () => {
      machine.send({
        type: 'abort',
      });
    },
    {
      once: true,
    },
  );

  return machine;
}
